'use client';
import React, { useState, useEffect, useRef } from 'react';

type Category = 'All' | 'Scripting' | 'Automations' | 'Integrations' | 'Schema Design';
type Difficulty = 'Beginner' | 'Intermediate' | 'Advanced';

interface Article {
  id: number;
  title: string;
  summary: string;
  category: Omit<Category, 'All'>;
  difficulty: Difficulty;
  readTime: string;
  tags: string[];
  forked?: number;
  updated: string;
}

const articles: Article[] = [
  {
    id: 1, title: 'selectRecordsAsync vs selectRecords: When 50ms becomes 5 seconds',
    summary: 'The async method is non-blocking but burns automation runs. Here\'s the decision matrix for high-volume bases.',
    category: 'Scripting', difficulty: 'Advanced', readTime: '9 min',
    tags: ['scripting', 'performance', 'async'], forked: 247, updated: 'Feb 24, 2026',
  },
  {
    id: 2, title: 'Building a real-time CRM status board with Automation triggers',
    summary: 'Three automation chains, one linked-record view, zero Zapier. A complete architecture walkthrough.',
    category: 'Automations', difficulty: 'Intermediate', readTime: '12 min',
    tags: ['automations', 'CRM', 'triggers'], forked: 183, updated: 'Feb 21, 2026',
  },
  {
    id: 3, title: 'Airtable REST API rate limits: a practical survival guide',
    summary: 'Five requests per second sounds fine until your Make scenario hits 400 records at 2 a.m.',
    category: 'Integrations', difficulty: 'Intermediate', readTime: '7 min',
    tags: ['API', 'rate-limits', 'Make'], forked: 329, updated: 'Feb 18, 2026',
  },
  {
    id: 4, title: 'Polymorphic linked records: the schema pattern agencies won\'t share',
    summary: 'One junction table, multiple parent types, infinite flexibility for repeatable client deliverables.',
    category: 'Schema Design', difficulty: 'Advanced', readTime: '11 min',
    tags: ['schema', 'linked-records', 'agency'], forked: 412, updated: 'Feb 15, 2026',
  },
  {
    id: 5, title: 'Writing your first Scripting Extension in 15 minutes',
    summary: 'From blank Monaco editor to a working batch-update script. No prior JavaScript expertise required.',
    category: 'Scripting', difficulty: 'Beginner', readTime: '15 min',
    tags: ['scripting', 'beginner', 'Monaco'], forked: 891, updated: 'Feb 12, 2026',
  },
  {
    id: 6, title: 'Sending Slack alerts from Automation scripts without CORS pain',
    summary: 'Automation scripts bypass CORS entirely. Here\'s the exact fetch() pattern for Slack webhook delivery.',
    category: 'Automations', difficulty: 'Beginner', readTime: '5 min',
    tags: ['automations', 'Slack', 'fetch'], forked: 156, updated: 'Feb 10, 2026',
  },
  {
    id: 7, title: 'Google Drive sync via Airtable API: a field-tested integration',
    summary: 'Attachment fields, Drive file IDs, and a background script that keeps both in sync without polling.',
    category: 'Integrations', difficulty: 'Advanced', readTime: '14 min',
    tags: ['integrations', 'Google Drive', 'API'], forked: 204, updated: 'Feb 7, 2026',
  },
  {
    id: 8, title: 'Designing for client handoff: self-documenting base architecture',
    summary: 'Field descriptions, view groupings, and color-coding conventions that make handoff a non-event.',
    category: 'Schema Design', difficulty: 'Beginner', readTime: '8 min',
    tags: ['schema', 'documentation', 'client'], forked: 567, updated: 'Feb 4, 2026',
  },
  {
    id: 9, title: 'LLM-generated scripts: what Airtable context you must provide',
    summary: 'GPT-4 writes decent scripts when you paste your schema. Here\'s the exact prompt template we use.',
    category: 'Scripting', difficulty: 'Intermediate', readTime: '10 min',
    tags: ['scripting', 'LLM', 'AI'], forked: 738, updated: 'Feb 1, 2026',
  },
];

const CATEGORIES: Category[] = ['All', 'Scripting', 'Automations', 'Integrations', 'Schema Design'];

export default function ArticleExplorer({ onFilterInteraction }: { onFilterInteraction: () => void }) {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [visibleCards, setVisibleCards] = useState<Article[]>(articles);
  const [animKey, setAnimKey] = useState(0);
  const interactedRef = useRef(0);

  const handleFilter = (cat: Category) => {
    setActiveCategory(cat);
    setAnimKey(k => k + 1);
    interactedRef.current += 1;
    if (interactedRef.current >= 3) {
      onFilterInteraction();
    }
    if (cat === 'All') {
      setVisibleCards(articles);
    } else {
      setVisibleCards(articles.filter(a => a.category === cat));
    }
  };

  return (
    <section id="explorer" className="py-24 px-6 max-w-7xl mx-auto">
      {/* Section header */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono text-xs tracking-widest uppercase" style={{ color: '#52525B' }}>// explore</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(124,58,237,0.15)' }} />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ color: '#F4F4F5' }}>
          Browse the knowledge base
        </h2>
        <p className="text-sm" style={{ color: '#71717A' }}>
          {visibleCards.length} articles · interact with filters to unlock the Reader app
        </p>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-10">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => handleFilter(cat)}
            className={`filter-chip px-4 py-2 rounded-full text-sm font-medium font-mono ${activeCategory === cat ? 'active' : ''}`}
          >
            {cat === 'All' ? '⬡ All' : cat}
          </button>
        ))}
      </div>

      {/* Card grid */}
      <div key={animKey} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {visibleCards.map((article, i) => (
          <ArticleCard key={article.id} article={article} delay={i * 60} />
        ))}
      </div>
    </section>
  );
}

function ArticleCard({ article, delay }: { article: Article; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'scale(0.95) translateY(12px)';
    const t = setTimeout(() => {
      el.style.transition = 'opacity 0.4s cubic-bezier(0.34,1.2,0.64,1), transform 0.4s cubic-bezier(0.34,1.2,0.64,1)';
      el.style.opacity = '1';
      el.style.transform = 'scale(1) translateY(0)';
    }, delay);
    return () => clearTimeout(t);
  }, [delay]);

  const diffClass = {
    Beginner: 'difficulty-beginner',
    Intermediate: 'difficulty-intermediate',
    Advanced: 'difficulty-advanced',
  }[article.difficulty];

  return (
    <div ref={ref} className="article-card rounded-2xl p-5 cursor-pointer group">
      {/* Top row */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="tag-pill">{article.category}</span>
        <span className={`tag-pill ${diffClass}`}>{article.difficulty}</span>
      </div>

      {/* Title */}
      <h3 className="font-semibold text-sm leading-snug mb-2" style={{ color: '#F4F4F5' }}>
        {article.title}
      </h3>

      {/* Summary (hover reveal) */}
      <div className="card-summary">
        <p className="text-xs leading-relaxed" style={{ color: '#71717A' }}>{article.summary}</p>
      </div>

      {/* Meta row */}
      <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: '1px solid rgba(124,58,237,0.1)' }}>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs" style={{ color: '#52525B' }}>{article.readTime}</span>
          {article.forked && (
            <span className="font-mono text-xs flex items-center gap-1" style={{ color: '#52525B' }}>
              <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
                <path d="M5 3.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm0 2.122a2.25 2.25 0 1 0-1.5 0v.878A2.25 2.25 0 0 0 5.75 8.5h1.5v2.128a2.251 2.251 0 1 0 1.5 0V8.5h1.5a2.25 2.25 0 0 0 2.25-2.25v-.878a2.25 2.25 0 1 0-1.5 0v.878a.75.75 0 0 1-.75.75h-4.5A.75.75 0 0 1 5 6.25v-.878zm3.75 7.378a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm3-8.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0z"/>
              </svg>
              {article.forked}
            </span>
          )}
        </div>
        <div className="read-badge">
          <span className="text-xs font-semibold flex items-center gap-1" style={{ color: '#7C3AED' }}>
            Read in App
            <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M11.5 8a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0zm-1.5 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
              <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"/>
            </svg>
            <svg width="8" height="8" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11.5 1a.5.5 0 0 1 0 1H11a.5.5 0 0 0-.5.5v11a.5.5 0 0 0 .5.5h.5a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1H5a.5.5 0 0 0 .5-.5v-11A.5.5 0 0 0 5 2h-.5a.5.5 0 0 1 0-1h7z"/>
            </svg>
          </span>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mt-3">
        {article.tags.map(tag => (
          <span key={tag} className="font-mono text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(196,181,253,0.06)', color: '#52525B' }}>
            #{tag}
          </span>
        ))}
      </div>
    </div>
  );
}